/**
 * @file 应用外壳：左侧导航 + 报头 + 内容区
 *
 * 菜单渲染规则（规范 4.4 / CONTRACT 第 4 章）：
 *   后端负责判定权限，前端只负责「没权限就不渲染入口」——不做假按钮、不做灰色不可点，
 *   避免出现「点了才报 403」的体验；直接输 URL 的越权访问由 PermissionRoute 兜到 /403。
 *
 * 版式：左侧 208px 一条细线分栏，顶栏只放刊名与当前账号，页面标题交给内容区，
 * 避免顶栏和页面标题各写一遍同一句话。
 */

import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth, useAuthState } from '../auth/context';
import logoUrl from '../../brand/logo.svg';

/** 一个导航项。 */
export interface NavItem {
  /** 路由路径。 */
  to: string;
  /** 菜单文案。 */
  label: string;
  /** 需要的功能权限码；缺省表示所有登录用户可见。 */
  permission?: string;
  /** 需要的角色 code（任一满足即可）；缺省不限。 */
  roles?: readonly string[];
  /** 右侧角标，如「D2」。 */
  tag?: string;
}

/** 外壳属性。 */
export interface AppShellProps {
  /** 端名，如「管理后台」。 */
  endName: string;
  /** 导航项（未过滤）。 */
  nav: readonly NavItem[];
  /** 内容区。 */
  children: ReactNode;
}

/**
 * @what 应用外壳
 * @param props.endName 端名
 * @param props.nav 导航项
 * @param props.children 内容区
 * @return JSX
 */
export function AppShell({ endName, nav, children }: AppShellProps) {
  const auth = useAuth();
  const state = useAuthState();
  const navigate = useNavigate();

  const identity = state.identity;

  /**
   * @what 判断菜单是否可见
   * @param item 菜单项
   * @return boolean true=渲染
   */
  const visible = (item: NavItem): boolean => {
    if (item.permission && !auth.has(item.permission)) return false;
    if (item.roles && item.roles.length > 0 && !item.roles.some((role) => auth.hasRole(role))) return false;
    return true;
  };

  const items = nav.filter(visible);
  const roleNames = identity?.roles?.map((role) => role.name).filter(Boolean).join(' / ') ?? '—';
  const initial = identity?.name?.slice(0, 1) ?? '?';

  /**
   * @what 退出登录并回登录页
   * @return Promise<void> 无返回值
   */
  const handleLogout = async (): Promise<void> => {
    await auth.logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="lx-shell">
      <aside className="lx-sidebar">
        <div className="lx-sidebar__brand">
          <img src={logoUrl} alt="词鲸英语" />
          <div>
            <div className="lx-sidebar__brand-name">词鲸英语</div>
            <div className="lx-sidebar__brand-end">{endName}</div>
          </div>
        </div>

        <div className="lx-nav__label">导航</div>
        <nav className="lx-nav" aria-label="主导航">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) => `lx-nav__item${isActive ? ' lx-nav__item--active' : ''}`}
            >
              <span>{item.label}</span>
              {item.tag ? <span className="lx-nav__tag">{item.tag}</span> : null}
            </NavLink>
          ))}
        </nav>

        <div className="lx-sidebar__footer">
          v1.0.0 · 内测
          <br />
          权限与数据范围由服务端判定
        </div>
      </aside>

      <div className="lx-main">
        <header className="lx-topbar">
          <span className="lx-topbar__title">
            词鲸英语 / <b>{endName}</b>
          </span>
          <span className="lx-topbar__spacer" />
          <div className="lx-user">
            <div className="lx-user__avatar" aria-hidden="true">
              {initial}
            </div>
            <div className="lx-user__meta">
              <span className="lx-user__name">{identity?.name ?? '未登录'}</span>
              <span className="lx-user__role">
                {roleNames}
                {identity?.masked_phone ? ` · ${identity.masked_phone}` : ''}
              </span>
            </div>
            <button type="button" className="lx-btn lx-btn--text" onClick={() => void handleLogout()}>
              退出
            </button>
          </div>
        </header>
        <main className="lx-content">{children}</main>
      </div>
    </div>
  );
}
